import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, Shield, ShieldAlert, ShieldCheck, ShieldOff, Loader2, X, AlertTriangle, Eye, Pencil, Trash2, FolderPlus } from 'lucide-react';
import { moveDocItem, moveDocItemPreview } from '../../../services/api';

/**
 * Modal that previews access changes before a cross-parent move and lets the
 * user choose whether to preserve current access (overrideAccess) or inherit
 * from the new parent.
 *
 * @shared:low
 */
export default function DocMovePreviewModal({ isOpen, moveData, onConfirm, onCancel }) {
    const [preview, setPreview] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [previewError, setPreviewError] = useState(null);

    const [moving, setMoving] = useState(false);
    const [preserveAccess, setPreserveAccess] = useState(true);

    const cancelRef = useRef(null);

    useEffect(() => {
        if (!isOpen || !moveData) return;
        setPreview(null);
        setPreviewError(null);
        setLoadingPreview(true);
        setPreserveAccess(true);

        let cancelled = false;
        moveDocItemPreview({
            itemId: moveData.item.id,
            itemType: moveData.item.type,
            targetParentId: moveData.toParentId,
        })
            .then((data) => { if (!cancelled) setPreview(data); })
            .catch((err) => { if (!cancelled) setPreviewError(err.message); })
            .finally(() => { if (!cancelled) setLoadingPreview(false); });

        return () => { cancelled = true; };
    }, [isOpen, moveData]);

    useEffect(() => {
        if (!isOpen) return;
        const handler = (e) => { if (e.key === 'Escape' && !moving) onCancel(); };
        document.addEventListener('keydown', handler);
        cancelRef.current?.focus();
        return () => document.removeEventListener('keydown', handler);
    }, [isOpen, moving, onCancel]);

    const handleConfirm = useCallback(async () => {
        if (!moveData) return;
        setMoving(true);
        try {
            await moveDocItem({
                itemId: moveData.item.id,
                itemType: moveData.item.type,
                targetParentId: moveData.toParentId,
                weight: 0,
                preserveAccess,
            });
            onConfirm?.();
        } catch (err) {
            console.error('[DocTree] Move failed:', err.message);
            setPreviewError('Le déplacement a échoué. Veuillez réessayer.');
        } finally {
            setMoving(false);
        }
    }, [moveData, preserveAccess, onConfirm]);

    if (!isOpen) return null;

    const diff = preview?.diff;
    const hasChanges = diff?.hasChanges;
    const isDir = moveData?.item?.type === 'directory';
    const itemLabel = isDir ? 'le répertoire' : 'la page';

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" role="dialog" aria-modal="true">
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" onClick={moving ? undefined : onCancel} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md border border-stone-200 overflow-hidden flex flex-col max-h-[85vh]">
                {/* Header */}
                <div className="px-5 pt-4 pb-3 flex items-start justify-between border-b border-stone-100 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full flex items-center justify-center bg-amber-50 flex-shrink-0">
                            <ArrowRight size={18} className="text-amber-500" />
                        </div>
                        <div>
                            <h3 className="text-sm font-semibold text-stone-900">Déplacer {itemLabel}</h3>
                            <p className="text-xs text-stone-500 mt-0.5">Impact sur les droits d'accès</p>
                        </div>
                    </div>
                    <button type="button" onClick={onCancel} disabled={moving} className="p-1 text-stone-400 hover:text-stone-600 transition-colors" aria-label="Fermer">
                        <X size={18} />
                    </button>
                </div>

                {/* Body — scrollable */}
                <div className="px-5 py-4 overflow-y-auto flex-1 min-h-0">
                    {loadingPreview && (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 size={22} className="animate-spin text-stone-400" />
                            <span className="ml-2 text-sm text-stone-500">Analyse des droits…</span>
                        </div>
                    )}

                    {previewError && (
                        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
                            <AlertTriangle size={16} className="flex-shrink-0" />
                            <span>{previewError}</span>
                        </div>
                    )}

                    {preview && !hasChanges && (
                        <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 text-green-700 text-sm">
                            <ShieldCheck size={16} className="flex-shrink-0" />
                            <span>Les droits d'accès restent identiques après déplacement.</span>
                        </div>
                    )}

                    {preview && hasChanges && (
                        <div className="space-y-3">
                            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 text-amber-700 text-xs leading-relaxed">
                                <ShieldAlert size={14} className="flex-shrink-0 mt-0.5" />
                                <span>Le nouvel emplacement a des droits d'accès différents. Voici les changements :</span>
                            </div>

                            {diff.lost?.length > 0 && (
                                <DiffSection
                                    icon={<ShieldOff size={11} />}
                                    title="N'auront plus accès"
                                    count={diff.lost.length}
                                    color="red"
                                >
                                    {diff.lost.map((g, i) => (
                                        <GrantRow key={i} grant={g} variant="lost" />
                                    ))}
                                </DiffSection>
                            )}

                            {diff.gained?.length > 0 && (
                                <DiffSection
                                    icon={<ShieldCheck size={11} />}
                                    title="Auront accès"
                                    count={diff.gained.length}
                                    color="green"
                                >
                                    {diff.gained.map((g, i) => (
                                        <GrantRow key={i} grant={g} variant="gained" />
                                    ))}
                                </DiffSection>
                            )}

                            {diff.changed?.length > 0 && (
                                <DiffSection
                                    icon={<Shield size={11} />}
                                    title="Permissions modifiées"
                                    count={diff.changed.length}
                                    color="amber"
                                >
                                    {diff.changed.map((c, i) => (
                                        <ChangedGrantRow key={i} change={c} />
                                    ))}
                                </DiffSection>
                            )}

                            {isDir && (
                                <div className="p-3 rounded-lg bg-stone-50 border border-stone-200">
                                    <label className="flex items-start gap-3 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={preserveAccess}
                                            onChange={(e) => setPreserveAccess(e.target.checked)}
                                            className="mt-0.5 accent-iris-600"
                                        />
                                        <div>
                                            <p className="text-xs font-medium text-stone-700">Conserver les droits actuels</p>
                                            <p className="text-[11px] text-stone-500 mt-0.5 leading-relaxed">
                                                L'héritage sera coupé et les droits effectifs actuels seront copiés en droits locaux.
                                            </p>
                                        </div>
                                    </label>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-5 py-3 bg-stone-50 border-t border-stone-100 flex items-center justify-end gap-2.5 flex-shrink-0">
                    <button
                        ref={cancelRef}
                        type="button"
                        onClick={onCancel}
                        disabled={moving}
                        className="btn btn-secondary btn-sm"
                    >
                        Annuler
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={moving || loadingPreview || !!previewError}
                        className="btn btn-primary btn-sm flex items-center gap-1.5"
                    >
                        {moving && <Loader2 size={13} className="animate-spin" />}
                        Confirmer le déplacement
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
}

/* ── Section wrapper with collapsible header ── */

const SECTION_COLORS = {
    red: { heading: 'text-red-600', border: 'border-red-100', badge: 'bg-red-100 text-red-600' },
    green: { heading: 'text-green-600', border: 'border-green-100', badge: 'bg-green-100 text-green-600' },
    amber: { heading: 'text-amber-600', border: 'border-amber-100', badge: 'bg-amber-100 text-amber-600' },
};

function DiffSection({ icon, title, count, color, children }) {
    const c = SECTION_COLORS[color] || SECTION_COLORS.amber;
    return (
        <div>
            <div className={`flex items-center gap-1.5 mb-1.5 ${c.heading}`}>
                {icon}
                <span className="text-[11px] font-semibold uppercase tracking-wider">{title}</span>
                <span className={`ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${c.badge}`}>{count}</span>
            </div>
            <div className={`border ${c.border} rounded-lg divide-y divide-stone-100 overflow-hidden`}>
                {children}
            </div>
        </div>
    );
}

/* ── Permission badge pills ── */

const PERM_DEFS = [
    { key: 'canView', label: 'Lecture', short: 'L', icon: Eye },
    { key: 'canUpdate', label: 'Écriture', short: 'É', icon: Pencil },
    { key: 'canDelete', label: 'Suppression', short: 'S', icon: Trash2 },
    { key: 'canCreateChildren', label: 'Création', short: 'C', icon: FolderPlus },
];

function PermBadges({ grant, className = '' }) {
    return (
        <div className={`flex items-center gap-1 ${className}`}>
            {PERM_DEFS.map(({ key, label, short, icon: Icon }) => (
                grant[key] ? (
                    <span key={key} title={label} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-stone-100 text-stone-600 text-[10px] font-medium leading-none">
                        <Icon size={10} className="opacity-60" />{short}
                    </span>
                ) : null
            ))}
        </div>
    );
}

/* ── Grant type labels ── */

const GRANT_TYPE_LABELS = {
    user: 'Utilisateur',
    project_role: 'Rôle projet',
    wiki_role: 'Rôle wiki',
};

function grantMeta(grant) {
    const name = grant.targetName || `#${grant.targetId}`;
    const type = GRANT_TYPE_LABELS[grant.grantType] || grant.grantType;
    const role = grant.roleId || '';
    if (grant.grantType === 'user') return { name, subtitle: '' };
    return { name, subtitle: role ? `${type} · ${role}` : type };
}

/* ── Grant row (lost / gained) ── */

function GrantRow({ grant, variant }) {
    const bg = variant === 'lost' ? 'bg-red-50/60' : 'bg-green-50/60';
    const { name, subtitle } = grantMeta(grant);

    return (
        <div className={`flex items-center justify-between gap-3 px-3 py-2 ${bg}`}>
            <div className="min-w-0">
                <div className="text-xs font-medium text-stone-800 truncate">{name}</div>
                {subtitle && <div className="text-[10px] text-stone-400 truncate">{subtitle}</div>}
            </div>
            <PermBadges grant={grant} className="flex-shrink-0" />
        </div>
    );
}

/* ── Changed grant row (before → after) ── */

function ChangedGrantRow({ change }) {
    const { name, subtitle } = grantMeta(change.subject || {});

    return (
        <div className="px-3 py-2 bg-amber-50/60">
            <div className="flex items-center gap-1.5 mb-1.5">
                <span className="text-xs font-medium text-stone-800 truncate">{name}</span>
                {subtitle && <span className="text-[10px] text-stone-400 truncate">({subtitle})</span>}
            </div>
            <div className="flex items-center gap-2">
                <PermBadges grant={change.before} className="opacity-50" />
                <ArrowRight size={10} className="text-stone-400 flex-shrink-0" />
                <PermBadges grant={change.after} />
            </div>
        </div>
    );
}
